import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAvatarColor } from "../utils/common";
import "../assets/css/formFields.css";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  return parts.length === 1
    ? parts[0].charAt(0).toUpperCase()
    : (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Pick a person from a team — avatar, name and email, the same way the Task
 * Assignment grid presents its user picker.
 *
 * A plain text select could not do this job: two of these names differ only
 * after the first few letters, and the email is what tells them apart. The
 * open-task count sits alongside so whoever is assigning can see who is
 * already loaded before they add to it.
 *
 * Props:
 *   members   : [{ UserCode, UserName, EMail, OpenTaskCount }]
 *   value     : selected UserCode, or "" for the team pool
 *   onChange  : (userCode) => void   — "" means leave it to the team
 *   disabled?, teamName?, poolLabel?
 */
const TeamMemberSelect = ({
  members = [],
  value,
  onChange,
  disabled = false,
  teamName,
  poolLabel = "Anyone in the team",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selected = useMemo(
    () => members.find((m) => m.UserCode === value) || null,
    [members, value],
  );

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isOpen]);

  /* Reopening after the team changed must not keep showing the old team's
     person as picked. */
  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const Avatar = ({ name, size = 34 }) => (
    <div
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "50%",
        backgroundColor: getAvatarColor(name),
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: size > 30 ? "0.8rem" : "0.7rem",
      }}
    >
      {getInitials(name)}
    </div>
  );

  /* Wears .sk-field__control, the same box as TextInput and SelectInput, so
     an "Assign to" sitting under a "Move to Team" is the same control with a
     richer inside rather than a different generation of one. It only sets
     what it needs on top: a taller line for the two-line avatar row. */
  return (
    <div className="sk-field" ref={wrapperRef} style={{ position: "relative" }}>
      <div
        onClick={() => !disabled && setIsOpen((s) => !s)}
        className={[
          "sk-field__control",
          isOpen ? "is-focused" : "",
          disabled ? "is-disabled" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          gap: "10px",
          minHeight: "54px",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {selected ? (
          <>
            <Avatar name={selected.UserName} />
            <div className="d-flex flex-column overflow-hidden">
              <span
                className="fw-bold text-dark text-truncate"
                style={{ fontSize: "0.88rem", lineHeight: 1.2 }}
              >
                {selected.UserName}
              </span>
              <span
                className="text-muted text-truncate"
                style={{ fontSize: "0.72rem" }}
              >
                {selected.EMail || "—"}
              </span>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                width: 34,
                height: 34,
                minWidth: 34,
                borderRadius: "50%",
                backgroundColor: "#e9ecef",
                border: "1px solid #dee2e6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6c757d",
              }}
            >
              <i className="bi bi-people-fill" />
            </div>
            <span style={{ fontSize: "0.88rem", color: "#495057" }}>
              {poolLabel}
            </span>
          </>
        )}

        <i
          className={`bi bi-chevron-down sk-field__affix ms-auto${
            isOpen ? " sk-field__affix--open" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            border: "1px solid #e9edf2",
            borderRadius: "12px",
            boxShadow:
              "0 4px 6px -2px rgba(16,24,40,0.03), 0 12px 28px -4px rgba(16,24,40,0.14)",
            zIndex: 1060,
            overflow: "hidden",
          }}
        >
          <div
            className="text-muted small fw-bold px-3 py-2 bg-light border-bottom"
            style={{ fontSize: "0.72rem" }}
          >
            {teamName ? `${teamName} team` : "Select user"}
            {members.length > 0 && ` · ${members.length}`}
          </div>

          <div style={{ maxHeight: "220px", overflowY: "auto" }}>
            {members.length === 0 ? (
              <div
                className="text-center text-muted py-4"
                style={{ fontSize: "0.8rem" }}
              >
                <i className="bi bi-people mb-2 d-block" />
                No members in this team
              </div>
            ) : (
              /* One list, no sections. The all-access people used to sit
                 under an "ALL TEAMS" divider on the grounds that they are not
                 really in this team — but they are: fn_UserVisibleTeams
                 grants every team to SUPER_ADMIN, ADMIN and TEAM_LEADER, so
                 the server will accept an assignment to any of them here just
                 as readily as to the roster. Splitting them out drew a line
                 the system does not draw. The API still marks them IsAllTeams
                 and still returns them after the roster, so the ordering is
                 unchanged — only the heading between the two is gone. */
              members.map((m) => {
                const isSel = m.UserCode === value;

                return (
                  <div
                    key={m.UserCode}
                    onClick={() => {
                      onChange?.(m.UserCode);
                      setIsOpen(false);
                    }}
                    className="d-flex align-items-center gap-2 px-3 py-2"
                    style={{
                      cursor: "pointer",
                      backgroundColor: isSel ? "#f0f7ff" : "transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSel) e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSel) e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <Avatar name={m.UserName} size={30} />

                    <div className="d-flex flex-column text-start overflow-hidden flex-grow-1">
                      <span
                        className="fw-bold text-dark text-truncate"
                        style={{ fontSize: "0.83rem", lineHeight: 1.2 }}
                      >
                        {m.UserName}
                      </span>
                      <span
                        className="text-muted text-truncate"
                        style={{ fontSize: "0.7rem" }}
                      >
                        {m.EMail || "—"}
                      </span>
                    </div>

                    {/* Workload, shown only when there is some. A "free" badge
                        on everybody with nothing on their plate put a label on
                        most rows in the list and made the few that carry real
                        load harder to pick out — which is the one thing this
                        badge exists to do. No badge now reads as free. */}
                    {m.OpenTaskCount > 0 && (
                      <span
                        className="badge rounded-pill"
                        style={{
                          backgroundColor: "#fff3e0",
                          color: "#e65100",
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.OpenTaskCount} open
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* The team pool, kept apart from the people so it reads as a
              different kind of choice rather than another name on the list. */}
          <div
            onClick={() => {
              onChange?.("");
              setIsOpen(false);
            }}
            className="text-center py-2 border-top"
            style={{
              cursor: "pointer",
              fontSize: "0.78rem",
              fontWeight: 600,
              color: value === "" ? "#4f46e5" : "#6c757d",
              backgroundColor: value === "" ? "#f0f7ff" : "transparent",
            }}
          >
            <i className="bi bi-people me-1" />
            {poolLabel}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberSelect;
